import React from 'react';
import { useMidiContext } from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import {
  LOCAL_AUDIO_BRIDGE_EVENT,
  LOCAL_AUDIO_GRADE_EVENT,
  type LocalAudioBridgeEventDetail,
  type LocalAudioGradeEventDetail,
} from 'Utils/LocalAudioSessionEvents';
import { useAudioRecognition } from '../AudioRecognitionContext';
import type { RecognitionFrame } from '../types';
import {
  buildLocalAudioSessionFrame,
  buildLocalAudioSessionReport,
  getLatestLocalAudioSession,
  LOCAL_AUDIO_SESSION_MAX_FRAMES,
  persistLocalAudioSession,
  type LocalAudioExpectedSnapshot,
  type LocalAudioSessionFrame,
  type LocalAudioSessionReport,
} from '../testing/localAudioSession';
import { AUDIO_SESSION_WAVEFORM_INTERVAL_MS } from '../testing/waveformCapture';

type LocalAudioSessionRecorderProps = {
  exercisePlaying: boolean;
  getExpectedMidiSnapshot: () => LocalAudioExpectedSnapshot;
  context: Record<string, unknown>;
};

type LocalAudioSessionsWindow = Window & {
  __museflowAudioSessions?: {
    latest: () => Promise<LocalAudioSessionReport | null>;
  };
};

const isLocalBrowser = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const newSessionId = () =>
  `${new Date().toISOString().replace(/[:.]/g, '-')}-${Math.random().toString(36).slice(2, 8)}`;

const SESSION_CHECKPOINT_INTERVAL_MS = 5_000;

const LocalAudioSessionRecorder = ({
  exercisePlaying,
  getExpectedMidiSnapshot,
  context,
}: LocalAudioSessionRecorderProps) => {
  const featureEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const { addListener, removeListener, calibrationProfile, running } = useAudioRecognition();
  const { preferredInputMode, audioInputLatencyMs } = useMidiContext();
  const captureAvailable =
    isLocalBrowser() &&
    featureEnabled &&
    preferredInputMode === 'audio' &&
    calibrationProfile != null &&
    running;

  const framesRef = React.useRef<LocalAudioSessionFrame[]>([]);
  const bridgeEventsRef = React.useRef<LocalAudioBridgeEventDetail[]>([]);
  const gradeEventsRef = React.useRef<LocalAudioGradeEventDetail[]>([]);
  const recordingRef = React.useRef(false);
  const startedAtMsRef = React.useRef(0);
  const sessionIdRef = React.useRef('');
  const truncatedRef = React.useRef(false);
  const contextRef = React.useRef(context);
  const profileRef = React.useRef(calibrationProfile);
  const exercisePlayingRef = React.useRef(exercisePlaying);
  const getExpectedMidiSnapshotRef = React.useRef(getExpectedMidiSnapshot);
  const persistQueueRef = React.useRef<Promise<void>>(Promise.resolve());
  const lastWaveformCaptureAtMsRef = React.useRef(0);

  contextRef.current = context;
  profileRef.current = calibrationProfile;
  exercisePlayingRef.current = exercisePlaying;
  getExpectedMidiSnapshotRef.current = getExpectedMidiSnapshot;

  const queueReport = React.useCallback((report: LocalAudioSessionReport, keepalive = false) => {
    persistQueueRef.current = persistQueueRef.current
      .catch(() => undefined)
      .then(() => persistLocalAudioSession(report, keepalive))
      .catch((error: unknown) => {
        console.warn(
          '[AudioRecognition] Could not write the captured session to the local workspace.',
          error,
        );
      });
  }, []);

  const persistCurrentSession = React.useCallback(
    (keepalive = false) => {
      if (framesRef.current.length === 0) return;
      const endedAtMs = Date.now();
      queueReport(
        buildLocalAudioSessionReport([...framesRef.current], {
          sessionId: sessionIdRef.current,
          startedAtMs: startedAtMsRef.current,
          endedAtMs,
          truncated: truncatedRef.current,
          metadata: {
            ...contextRef.current,
            audioInputLatencyMs,
          },
          calibrationProfile: profileRef.current,
          bridgeEvents: [...bridgeEventsRef.current],
          gradeEvents: [...gradeEventsRef.current],
        }),
        keepalive,
      );
    },
    [audioInputLatencyMs, queueReport],
  );

  const finishSession = React.useCallback(
    (keepalive = false) => {
      if (!recordingRef.current) return;
      recordingRef.current = false;
      persistCurrentSession(keepalive);
    },
    [persistCurrentSession],
  );

  React.useEffect(() => {
    if (!isLocalBrowser()) return;
    (window as LocalAudioSessionsWindow).__museflowAudioSessions = {
      latest: getLatestLocalAudioSession,
    };
  }, []);

  React.useEffect(() => {
    if (!isLocalBrowser() || !featureEnabled) return;

    const listener = {
      name: 'local-audio-session-recorder',
      onFrame: (frame: RecognitionFrame) => {
        if (!recordingRef.current) return;

        const capturedAtMs = Date.now();

        let expected: LocalAudioExpectedSnapshot = { midiNotes: [] };
        try {
          expected = getExpectedMidiSnapshotRef.current();
        } catch (error) {
          console.warn(
            '[AudioRecognition] Could not read expected notes for local capture.',
            error,
          );
        }

        const captureWaveform =
          capturedAtMs - lastWaveformCaptureAtMsRef.current >= AUDIO_SESSION_WAVEFORM_INTERVAL_MS;
        if (captureWaveform) {
          lastWaveformCaptureAtMsRef.current = capturedAtMs;
        }
        framesRef.current.push(
          buildLocalAudioSessionFrame(
            frame,
            expected,
            exercisePlayingRef.current,
            capturedAtMs,
            captureWaveform,
          ),
        );
        if (framesRef.current.length >= LOCAL_AUDIO_SESSION_MAX_FRAMES) {
          truncatedRef.current = true;
          finishSession();
        }
      },
      wantsWaveform: true,
    };

    addListener(listener);
    return () => removeListener(listener);
  }, [addListener, featureEnabled, finishSession, removeListener]);

  React.useEffect(() => {
    if (!isLocalBrowser() || !featureEnabled) return;

    const onBridgeEvent = (event: Event) => {
      if (!recordingRef.current) return;
      bridgeEventsRef.current.push((event as CustomEvent<LocalAudioBridgeEventDetail>).detail);
    };
    const onGradeEvent = (event: Event) => {
      if (!recordingRef.current) return;
      gradeEventsRef.current.push((event as CustomEvent<LocalAudioGradeEventDetail>).detail);
    };

    window.addEventListener(LOCAL_AUDIO_BRIDGE_EVENT, onBridgeEvent);
    window.addEventListener(LOCAL_AUDIO_GRADE_EVENT, onGradeEvent);
    return () => {
      window.removeEventListener(LOCAL_AUDIO_BRIDGE_EVENT, onBridgeEvent);
      window.removeEventListener(LOCAL_AUDIO_GRADE_EVENT, onGradeEvent);
    };
  }, [featureEnabled]);

  React.useEffect(() => {
    if (exercisePlaying && captureAvailable && !recordingRef.current) {
      framesRef.current = [];
      bridgeEventsRef.current = [];
      gradeEventsRef.current = [];
      startedAtMsRef.current = Date.now();
      lastWaveformCaptureAtMsRef.current =
        startedAtMsRef.current - AUDIO_SESSION_WAVEFORM_INTERVAL_MS;
      sessionIdRef.current = newSessionId();
      truncatedRef.current = false;
      profileRef.current = calibrationProfile;
      recordingRef.current = true;
      return;
    }

    if ((!exercisePlaying || !captureAvailable) && recordingRef.current) {
      finishSession();
    }
  }, [calibrationProfile, captureAvailable, exercisePlaying, finishSession]);

  React.useEffect(() => {
    if (!isLocalBrowser() || !featureEnabled) return;
    const checkpointId = window.setInterval(() => {
      if (recordingRef.current) {
        persistCurrentSession();
      }
    }, SESSION_CHECKPOINT_INTERVAL_MS);

    return () => window.clearInterval(checkpointId);
  }, [featureEnabled, persistCurrentSession]);

  React.useEffect(() => {
    const flushBeforeUnload = () => finishSession(true);
    window.addEventListener('pagehide', flushBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', flushBeforeUnload);
      finishSession(true);
    };
  }, [finishSession]);

  return null;
};

export default LocalAudioSessionRecorder;
